import { Link, useParams } from "react-router-dom";
import Seo, { breadcrumbLd, faqLd } from "../lib/seo";
import { INDUSTRIES, PRODUCTS } from "../data/content";
import { Breadcrumbs } from "../components/site/Layout";
import { PageHero, CtaBand, RelatedGrid } from "../components/site/Sections";
import { Reveal, Overline } from "../components/site/Reveal";
import NotFound from "./NotFound";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { Pill, Sparkles, UtensilsCrossed, Cpu, Gem, Car, Cog, ShoppingBag, Package, Briefcase } from "lucide-react";

export const INDUSTRY_ICONS = {
  "pharmaceutical-hologram-labels": Pill,
  "cosmetic-security-labels": Sparkles,
  "food-product-security-labels": UtensilsCrossed,
  "electronics-security-labels": Cpu,
  "luxury-product-hologram-labels": Gem,
  "automotive-security-labels": Car,
  "spare-parts-security-labels": Cog,
  "consumer-product-security-labels": ShoppingBag,
  "packaging-security-labels": Package,
  "corporate-security-stickers": Briefcase,
};

export function Industries() {
  return (
    <>
      <Seo title="Hologram Security Solutions for UAE Industries | NanoMax"
        description="Hologram and security label solutions tailored for UAE industries including pharmaceuticals, cosmetics, electronics, automotive, luxury goods and more."
        path="/industries" jsonLd={breadcrumbLd([{ name: "Home", path: "/" }, { name: "Industries", path: "/industries" }])} />
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Industries" }]} />
      <PageHero overline="Industries" title="Hologram Security Solutions for UAE Industries"
        subtitle="Security label solutions adapted to the authentication and packaging needs of each industry we serve across the UAE." />
      <section className="px-6 md:px-10 lg:px-16 py-20 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {INDUSTRIES.map((ind, i) => {
          const Icon = INDUSTRY_ICONS[ind.slug];
          return (
            <Reveal key={ind.slug} delay={i * 0.04}>
              <Link to={`/industries/${ind.slug}`} className="card p-7 h-full block group" data-testid={`industry-card-${i}`}>
                <div className="w-12 h-12 flex items-center justify-center border border-white/10 mb-5 group-hover:border-holo-cyan transition-colors">
                  {Icon && <Icon size={22} className="text-[#8A8A9B] group-hover:text-holo-cyan transition-colors" />}
                </div>
                <h2 className="font-head text-xl font-semibold group-hover:text-holo-cyan transition-colors">{ind.name}</h2>
                <p className="text-sm text-[#8A8A9B] mt-3 leading-relaxed">{ind.tile}</p>
                <span className="link-arrow mt-5 inline-flex">Explore {ind.name} Labels <span aria-hidden>&rarr;</span></span>
              </Link>
            </Reveal>
          );
        })}
      </section>
      <CtaBand sourcePage="/industries" />
    </>
  );
}

export function IndustryDetail() {
  const { slug } = useParams();
  const ind = INDUSTRIES.find((x) => x.slug === slug);
  if (!ind) return <NotFound />;
  const path = `/industries/${ind.slug}`;
  const Icon = INDUSTRY_ICONS[ind.slug];
  const faqs = [
    { q: `What hologram labels suit ${ind.name.toLowerCase()} in the UAE?`, a: `We recommend a mix of features based on your products — commonly tamper-evident seals, serialised labels and QR verification for ${ind.name.toLowerCase()}.` },
    { q: `Can NanoMax customize labels for our ${ind.name.toLowerCase()} packaging?`, a: `Yes. Labels are produced around your artwork, packaging format and authentication needs, with custom sizes and finishes.` },
  ];
  return (
    <>
      <Seo title={`${ind.name} Security Labels in UAE | NanoMax Holo Solutions`} description={ind.desc} path={path}
        jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "Industries", path: "/industries" }, { name: ind.name, path }]), faqLd(faqs)]} />
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Industries", path: "/industries" }, { name: ind.name }]} />
      <PageHero overline={<span className="inline-flex items-center gap-2">{Icon && <Icon size={14} className="text-holo-cyan" />} Industry</span>} title={`Hologram Security Labels for ${ind.name}`} subtitle={ind.desc} />
      <section className="px-6 md:px-10 lg:px-16 py-16 grid lg:grid-cols-2 gap-10">
        <Reveal>
          <div className="card p-8 h-full">
            <Overline>Typical Applications</Overline>
            <ul className="mt-5 space-y-3">
              {ind.apps.map((a) => <li key={a} className="text-[#C9C9D4] flex gap-3"><span className="text-holo-cyan">—</span>{a}</li>)}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="card p-8 h-full">
            <Overline>Why NanoMax</Overline>
            <p className="text-[#8A8A9B] mt-5 leading-relaxed">Based in Sharjah Media City, NanoMax works with {ind.name.toLowerCase()} businesses across the UAE to design holographic security labels that balance authentication, tamper evidence and premium presentation — customized to your products.</p>
          </div>
        </Reveal>
      </section>
      <section className="px-6 md:px-10 lg:px-16 py-16 border-t border-white/10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4"><Overline>FAQ</Overline><h2 className="font-head text-2xl md:text-3xl font-bold mt-4">{ind.name} questions.</h2></div>
        <div className="lg:col-span-8">
          <Accordion type="single" collapsible>
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`f${i}`} className="border-white/10">
                <AccordionTrigger className="text-left font-head hover:text-holo-cyan hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#8A8A9B] text-sm leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
      <RelatedGrid title="Recommended Products" items={PRODUCTS.slice(0, 4).map((x) => ({ name: x.name, desc: x.tile, to: `/products/${x.slug}` }))} />
      <CtaBand sourcePage={path} />
    </>
  );
}
